/**
 * Feuille LIBRE d'une question isolée (Warmup, diagnostic, automatismes, test
 * de pack, démo…) : pour calculer, sans HORA, calculs vérifiés. Ses lignes sont
 * gardées sur l'appareil (`storageKey`, rien en base) — clé
 * `jr_free_scratch:<écran>:<session>:<question>`.
 */

import { useEffect, useMemo, useState } from 'react';
import type { FreeModelInput, V2Session } from '@/features/brouillon-v2';
import { ScratchSession, type SheetLine } from './scratch-session';
import { loadFreeLines, loadFreeTextLines, saveFreeLines, saveFreeTextLines } from './free-sheet';
import { loadV2Ui, type V2Ui } from './scratch-data';

export function freeSheetKey(
  kind: string,
  sessionId: string | null | undefined,
  questionId: string,
): string | null {
  return sessionId ? `jr_free_scratch:${kind}:${sessionId}:${questionId}` : null;
}

function stored(l: SheetLine) {
  return { atoms: l.atoms, unchecked: !!l.unchecked };
}

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/** Une session par question (`questionKey`), verrouillée une fois la question répondue. */
export function useFreeSheet({
  questionKey,
  numbers,
  storageKey,
  locked,
}: {
  questionKey: string;
  /** nombres de l'énoncé (bouts de la palette) */
  numbers: string[];
  storageKey: string | null;
  locked: boolean;
}): ScratchSession {
  const session = useMemo(() => {
    const s: ScratchSession = new ScratchSession({
      free: true,
      numbers,
      calculator: false,
      stepDelayMs: prefersReducedMotion() ? 60 : 300,
      onCommit: storageKey
        ? () => saveFreeLines(storageKey, s.getSnapshot().lines.map(stored))
        : undefined,
      onErase: storageKey
        ? () => saveFreeLines(storageKey, s.getSnapshot().lines.map(stored))
        : undefined,
    });
    s.restoreFree(loadFreeLines(storageKey));
    return s;
    // une session par question : les nombres et la clé en découlent
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionKey]);
  useEffect(() => () => session.dispose(), [session]);
  useEffect(() => {
    if (locked) session.lock();
  }, [locked, session]);
  return session;
}

/** L'interface du moteur v2, chargée une fois pour toutes les feuilles libres de la page. */
function useV2UiModule(enabled: boolean): V2Ui | null {
  const [ui, setUi] = useState<V2Ui | null>(null);
  useEffect(() => {
    if (!enabled || ui) return;
    let alive = true;
    loadV2Ui()
      .then((m) => {
        if (alive) setUi(() => m);
      })
      .catch((e) => console.warn('[scratch] interface v2 indisponible (feuille libre)', e));
    return () => {
      alive = false;
    };
  }, [enabled, ui]);
  return ui;
}

/**
 * Feuille libre du moteur v2 pour une question isolée : construite à partir de son énoncé et de ses propositions
 * (`freeV2Model`), sans HORA ; lignes gardées sur l'appareil (`storageKey`). null tant que l'interface n'est pas
 * chargée, ou si elle ne l'est jamais : l'écran garde alors la feuille libre classique (`useFreeSheet`).
 */
export function useFreeV2Sheet({
  questionKey,
  input,
  storageKey,
  locked,
  enabled = true,
}: {
  questionKey: string;
  input: FreeModelInput;
  storageKey: string | null;
  locked: boolean;
  enabled?: boolean;
}): { ui: V2Ui; session: V2Session } | null {
  const ui = useV2UiModule(enabled);
  const session = useMemo(() => {
    if (!ui) return null;
    try {
      const { model, hideStart, bits } = ui.freeV2Model(input);
      const s: V2Session = new ui.V2Session({
        model,
        free: true,
        hideStart,
        statementBits: bits,
        onCommit: () => {
          saveFreeTextLines(
            storageKey,
            s.lines.filter((l) => !l.stopped).map((l) => ({ text: l.text })),
          );
          return 0;
        },
      });
      const stored = loadFreeTextLines(storageKey);
      if (stored.length)
        s.replay(stored.map((l) => ({ text: l.text, kind: 'kept', hora: 0, hinted: false })));
      return s;
    } catch (e) {
      console.warn('[scratch] feuille libre v2 ignorée', e);
      return null;
    }
    // une feuille par question : l'énoncé et la clé en découlent
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ui, questionKey]);
  useEffect(() => {
    if (locked && session && !session.locked) session.lock();
  }, [locked, session]);
  return ui && session ? { ui, session } : null;
}
